import { hasPostalInput, validatePostalList } from './postalCriterion';

/**
 * Checks the UI filter state before a search is started or extended.
 * @param {object} filters - UI filter state (DEFAULT_FILTERS shape)
 * @param {'maps'|'hr'} searchType
 * @returns {{title: string, message: string}|null} The first problem as a toast payload, or null when valid
 */
export const validateSearchFilters = (filters, searchType) => {
	// Validate: at least industrySearchTerms/industryCodes/location must be set
	const hasIndustryInput =
		(filters.industrySearchTerms && filters.industrySearchTerms.trim().length > 0) ||
		(Array.isArray(filters.industryCodes) && filters.industryCodes.length > 0);

	if (!hasIndustryInput) {
		if (searchType === 'maps') {
			return { title: 'Eingabe erforderlich', message: 'Bitte geben Sie mindestens eine Branche ein.' };
		}
	}

	// PLZ mode: a from/to range and/or a free-text list — at least one, each valid.
	const isPlzMode = filters.locationMode === 'plz';
	if (isPlzMode) {
		const hasFrom = Boolean(filters.plzFrom);
		const hasTo = Boolean(filters.plzTo);
		const plzPattern = /^\d{5}$/;
		if (hasFrom || hasTo) {
			if (!plzPattern.test(filters.plzFrom) || !plzPattern.test(filters.plzTo)) {
				return {
					title: 'Ungültiger PLZ-Bereich',
					message:
						'Bitte geben Sie zwei fünfstellige Postleitzahlen an (von/bis) oder lassen Sie beide leer.',
				};
			}
			if (parseInt(filters.plzFrom, 10) > parseInt(filters.plzTo, 10)) {
				return {
					title: 'Ungültiger PLZ-Bereich',
					message: 'Die Start-PLZ darf nicht größer als die End-PLZ sein.',
				};
			}
		}
		const listCheck = validatePostalList(filters.plzList);
		if (!listCheck.valid) {
			return {
				title: 'Ungültige PLZ-Eingabe',
				message: `Ungültig: ${listCheck.invalid.join(', ')}. Erlaubt sind PLZ (38124), Bereiche (38100-38200) und Präfixe (38*), per Komma getrennt.`,
			};
		}
		if (!hasPostalInput(filters)) {
			return {
				title: 'PLZ erforderlich',
				message: 'Bitte geben Sie einen PLZ-Bereich oder eine PLZ-Liste an.',
			};
		}
	}

	const isStateMode = filters.locationMode === 'state';
	if (isStateMode && filters.states.length === 0) {
		return { title: 'Bundesland erforderlich', message: 'Bitte wählen Sie mindestens ein Bundesland aus.' };
	}

	// Without a location the search covers the whole selected country
	const isPlaceMode = !isPlzMode && !isStateMode;

	if (!hasIndustryInput) {
		const hasHRFilter =
			(isPlaceMode && filters.location) ||
			isPlzMode ||
			isStateMode ||
			filters.searchKeywords?.trim() ||
			filters.legalForm.length > 0 ||
			filters.revenueFrom ||
			filters.revenueTo ||
			filters.balanceTotalFrom ||
			filters.balanceTotalTo ||
			filters.earningsFrom ||
			filters.earningsTo ||
			filters.employeesFrom ||
			filters.employeesTo ||
			filters.eventType;
		if (!hasHRFilter) {
			return {
				title: 'Filter erforderlich',
				message: 'Bitte geben Sie mindestens eine Branche oder einen anderen Filter an.',
			};
		}
	}

	// Validate range consistency (from <= to)
	const rangeChecks =
		searchType === 'maps'
			? [{ from: filters.ratingFrom, to: filters.ratingTo, label: 'Bewertung' }]
			: [
					{ from: filters.revenueFrom, to: filters.revenueTo, label: 'Umsatz' },
					{ from: filters.balanceTotalFrom, to: filters.balanceTotalTo, label: 'Bilanzsumme' },
					{ from: filters.earningsFrom, to: filters.earningsTo, label: 'Gewinn' },
					{ from: filters.employeesFrom, to: filters.employeesTo, label: 'Mitarbeiteranzahl' },
				];

	for (const { from, to, label } of rangeChecks) {
		if (from && to && parseFloat(from) > parseFloat(to)) {
			return {
				title: 'Ungültiger Bereich',
				message: `Der Min-Wert für „${label}" darf nicht größer als der Max-Wert sein.`,
			};
		}
	}

	return null;
};
